import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { School, Save, CheckCircle2, AlertCircle, RefreshCw, Building, MapPin, Mail, Phone } from 'lucide-react';

export const SchoolProfilePage: React.FC = () => {
  const { school, refreshMe, fetchWithAuth } = useAuth();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    school_name: '',
    school_code: '',
    district: 'Nuh',
    state: 'Haryana',
    address: '',
    official_email: '',
    active_session_id: 1
  });

  const [sessions, setSessions] = useState<Array<{ id: number; name: string; is_active: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Load current school & sessions
    if (school) {
      setFormData({
        school_name: school.school_name || '',
        school_code: school.school_code || '',
        district: school.district || 'Nuh',
        state: school.state || 'Haryana',
        address: school.address || '',
        official_email: school.official_email || '',
        active_session_id: school.active_session_id || 1
      });
    }

    fetchWithAuth('/api/academic-sessions')
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSessions(data.sessions);
        }
      })
      .catch(console.error);
  }, [school]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!formData.school_name.trim() || !formData.school_code.trim() || !formData.district.trim() || !formData.state.trim()) {
      setMessage({ type: 'error', text: 'School name, school code, district, and state are required.' });
      return;
    }

    setSaving(true);
    try {
      const res = await fetchWithAuth('/api/school', {
        method: 'PUT',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'School profile updated and persisted successfully in database.' });
        await refreshMe();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update school profile.' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-6 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#0F2C59] text-white rounded-xl shadow-xs">
            <School className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              {t('nav_school_profile')}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Manage institution identification, address, and academic session settings
            </p>
          </div>
        </div>
        <span className="bg-amber-100 text-amber-900 text-xs font-bold px-2.5 py-1 rounded-full border border-amber-300">
          Official Record
        </span>
      </div>

      {/* Alert Messages */}
      {message && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center space-x-2.5 border ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
              : 'bg-rose-50 text-rose-800 border-rose-300'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span className="font-semibold">{message.text}</span>
        </div>
      )}

      {/* Edit Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('schoolName')} <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.school_name}
                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                placeholder="e.g. Government Senior Secondary School, Nuh"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('schoolCode')} (UDISE / State ID) <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.school_code}
                onChange={(e) => setFormData({ ...formData, school_code: e.target.value })}
                placeholder="e.g. HR-NUH-0001"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('district')} <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('state')} <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('officialEmail')}
              </label>
              <input
                type="email"
                value={formData.official_email}
                onChange={(e) => setFormData({ ...formData, official_email: e.target.value })}
                placeholder="e.g. gmssss.nuh@hry.gov.in"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('schoolAddress')}
              </label>
              <textarea
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Complete address including pin code..."
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                {t('activeSession')}
              </label>
              <select
                value={formData.active_session_id}
                onChange={(e) => setFormData({ ...formData, active_session_id: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} {s.is_active ? '(Active)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2.5 px-5 rounded-lg shadow-sm flex items-center space-x-2 transition disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-amber-400" />}
              <span>{saving ? t('saving') : t('save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
